/* eslint-disable promise/always-return */
import 'jquery';
import publishService from 'tradera-apps/syi/script/app_react/services/publish-service';
import navigationService from 'tradera-apps/syi/script/app_react/services/navigation-service';
import draftService from 'tradera-apps/syi/script/app_react/services/draft-service';
import categoryActions from './category-actions';
import syiActions from './syi-actions';
import paymentActions from './payment-actions';
import imageActions from './image-actions';
import priceActions from './price-actions';
import { shortDescriptionActions } from './short-description-actions';
import { setDescriptionLanguageCodeIso2 } from 'tradera-state/views/syi/reducers/short-description';
import longDescriptionActions from './long-description-actions';
import shippingActions from './shipping-actions';
import campaignCodeActions from './campaign-code-actions';
import {
    resetCharityAndShippingInfo,
    setCharityAndFetchInfo,
    setCharityFromDraftAndFetchInfo
} from 'tradera-state/views/syi/actions/charity-actions';
import feesActions from './fees-actions';
import {
    setAttributesFromDraft,
    resetImageAttributesSuggestions
} from './attributes-actions';
import scribe from 'tradera-apps/syi/script/app_react/services/scribe';
import sessionService from 'tradera-apps/syi/script/app_react/services/session-service';
import googleTagManager from 'tradera-apps/syi/script/app_react/services/google-tagmanager';
import {
    setAllowedBuyerRegion,
    setAvailableAllowedBuyerRegions
} from '../reducers/buyer-region';
import { handleServerValidationError } from './helpers/server-validation-error-helper';
import { getSyiSlice } from 'tradera-state/views/syi/selectors/syi';
import { selectSuggestedAttributes } from 'tradera-state/views/syi/selectors/attribute-selectors';
import initData from 'init-data';

function ensureDraftReason(draft) {
    if (!draft.draftReason) {
        draft.draftReason = 'NewItem';
    }
}

const setDraft = (draft, params = {}) => (dispatch, getState) => {
    dispatch(syiActions.disableAutoSave());
    ensureDraftReason(draft);
    sessionService.startSession(draft.draftReason);

    let isNewDraft =
        getSyiSlice(getState()).draft.draftId != draft.draftId ||
        getSyiSlice(getState()).draft.draftReason != draft.draftReason;
    let isChangeItem = draft.draftReason === 'ChangeItem';

    // Do first for actions that depends on this info.
    dispatch(syiActions.setIsEditOfPublishedItem(isChangeItem));
    dispatch(syiActions.hideAllErrors());
    dispatch(categoryActions.reset());

    dispatch({
        type: 'SET_DRAFT_PROPERTIES',
        draft: draft
    });

    dispatch(
        setAllowedBuyerRegion({
            allowedBuyerRegion: draft.allowedBuyerRegion
        })
    );
    dispatch(setAvailableAllowedBuyerRegions());
    dispatch(setItemCondition(draft.itemCondition));
    dispatch(
        campaignCodeActions.setInitialCampaignCode(
            draft.campaignCode,
            draft.campaignCodeId,
            draft.campaignCodeDescription,
            isChangeItem
        )
    );
    dispatch(setDurationDays(draft.durationDays));
    dispatch(setCustomEndTime(draft.customEndTime));

    dispatch(
        categoryActions.setPath(draft.shortDescription, draft.categoryPath)
    );

    // Setting short description also loads top categories!
    dispatch(
        shortDescriptionActions.setShortDescription(draft.shortDescription)
    );
    dispatch(setDescriptionLanguageCodeIso2(draft.descriptionLanguageCodeIso2));
    dispatch(setCurrency(draft.currency));
    dispatch(categoryActions.setDraftCategory(draft.categoryId));
    dispatch(
        longDescriptionActions.setLongDescriptionFromDraft(
            draft.longDescription
        )
    );
    dispatch(setDisplayPromoted(draft.displayPromoted));
    dispatch(setDisplayHighlighted(draft.displayHighlighted));
    dispatch(imageActions.setDraftImages(draft.images || []));
    dispatch(resetImageAttributesSuggestions());
    dispatch(
        priceActions.setFromDraft(
            draft.openingBid,
            draft.fixedPrice,
            draft.reservePrice,
            draft.charity?.charityId,
            draft.vat,
            isChangeItem
        )
    );

    if (initData.featureSwitches['pennybridge-charities']) {
        if (params.charityId) {
            dispatch(setCharityAndFetchInfo(params.charityId));
        } else if (draft.charity) {
            dispatch(setCharityFromDraftAndFetchInfo(draft.charity));
        }
    }
    dispatch(paymentActions.setSelectedPaymentOptions(draft.paymentOptions));
    dispatch(setAttributesFromDraft(draft));

    const shippingOptions = getShippingOptionsFromDraft(
        draft.shippingOptions,
        draft.fromCountry,
        initData.featureSwitches['shipping-from-country']
    );

    const fromCountry = getFromCountryFromDraft(
        draft.fromCountry,
        initData.memberCountryCodeIso2,
        initData.featureSwitches['shipping-from-country']
    );

    dispatch(
        shippingActions.setShippingFromcountryFromDraft({
            fromCountry
        })
    );

    if (initData.featureSwitches['shipping-by-weight']) {
        dispatch(
            shippingActions.getAndSetShippingOptionsByWeight(
                draft.shippingOptions,
                fromCountry
            )
        );
    } else {
        dispatch(shippingActions.setShippingOptionsFromDraft(shippingOptions));
    }

    dispatch(syiActions.enableAutoSave());

    if (isNewDraft) {
        scribe.sendSessionStart();
    }
};

export const getShippingOptionsFromDraft = (
    shippingOptions,
    fromCountry,
    isShippingFromCountryEnabled
) => {
    return isShippingFromCountryEnabled && fromCountry
        ? shippingOptions
        : (fromCountry && fromCountry === 'SE') || !fromCountry
        ? shippingOptions
        : [];
};

export const getFromCountryFromDraft = (
    fromCountry,
    memberCountryCodeIso2,
    isShippingFromCountryEnabled
) => {
    return isShippingFromCountryEnabled && fromCountry
        ? fromCountry
        : isShippingFromCountryEnabled && memberCountryCodeIso2 === 'DK'
        ? memberCountryCodeIso2
        : 'SE';
};

const setIsLoading = isLoading => ({
    type: 'SET_DRAFT_IS_LOADING',
    payload: isLoading
});

const loadDraft = (dispatch, draftId) => {
    dispatch(setIsLoading(true));
    return draftService
        .loadDraft(draftId)
        .then(result => {
            dispatch(setDraft(result));
            return result;
        })
        .catch(result => {
            navigationService.goToStart();
            throw result;
        })
        .finally(() => dispatch(setIsLoading(false)));
};

const loadDrafts = () => dispatch => {
    return draftService.loadDrafts().then(drafts => {
        let resumableDrafts = [];

        drafts.forEach(draft => {
            if (
                canBeResumed(
                    draft.draftId,
                    draft.draftReason,
                    draft.shortDescription,
                    draft.longDescription,
                    [{ imageUrl: draft.imageUrl }]
                )
            ) {
                resumableDrafts.push(draft);
            }
        });

        dispatch(setResumableDrafts(resumableDrafts));
        return resumableDrafts;
    });
};

const loadImagesFromDraft = draftId => async (dispatch, getState) => {
    const browserDraftImageUrls = (getSyiSlice(getState()).images.slots || [])
        .map(image => image.imageUrl)
        .filter(Boolean);

    const serverDraft = await draftService.loadDraft(draftId);
    const serverDraftImages = serverDraft.images || [];
    const serverDraftImageUrls = serverDraftImages.map(image => image.imageUrl);

    const changedImages = browserDraftImageUrls
        .filter(x => !serverDraftImageUrls.includes(x))
        .concat(
            serverDraftImageUrls.filter(x => !browserDraftImageUrls.includes(x))
        );
    const imagesHaveChanged = changedImages.length > 0;
    if (imagesHaveChanged) {
        dispatch(imageActions.setDraftImages(serverDraftImages));
    }
    return imagesHaveChanged;
};

const canBeResumed = (
    draftId,
    draftReason,
    shortDescription,
    longDescription,
    images
) => {
    if (!draftId) {
        return false;
    }

    if (draftReason !== 'NewItem') {
        return false;
    }

    var hasShortDescription =
        shortDescription && shortDescription.trim().length > 0;
    var hasLongDescription =
        longDescription && longDescription.trim().length > 0;
    var hasImage =
        Array.isArray(images) && images.some(image => !!image.imageUrl);
    return hasShortDescription || hasLongDescription || hasImage;
};

const updateDraftCanBeResumed = () => (dispatch, getState) => {
    let draft = getSyiSlice(getState()).draft;
    let shortDescription = getSyiSlice(getState()).shortDescription
        .shortDescription;
    let longDescription = getSyiSlice(getState()).longDescription
        .longDescription;
    let imageSlots = getSyiSlice(getState()).images.slots;

    // TODO: avoid duplication by using canBeResumed
    if (!draft.draftId) {
        return false;
    }

    if (draft.draftReason !== 'NewItem') {
        return false;
    }

    var hasShortDescription =
        shortDescription && shortDescription.trim().length > 0;
    var hasLongDescription =
        longDescription && longDescription.trim().length > 0;
    var hasImage =
        Array.isArray(imageSlots) && imageSlots.some(image => !!image.imageUrl);
    let canBeResumed = hasShortDescription || hasLongDescription || hasImage;

    dispatch({
        type: 'SET_DRAFT_CAN_BE_RESUMED',
        payload: canBeResumed
    });
};

const setResumableDrafts = drafts => dispatch => {
    dispatch({
        type: 'SET_DRAFTS',
        payload: drafts
    });
};

const setCustomEndTime = customEndTime => (dispatch, getState) => {
    const price = getSyiSlice(getState()).price;
    const hasOpeningBid = !!price.openingBid;
    const hasAuctionBinPrice = !!price.auctionBinPrice;
    const isAuctionBin = hasOpeningBid && hasAuctionBinPrice;
    const isPureBin = !hasOpeningBid && hasAuctionBinPrice;
    dispatch({
        type: 'SET_CUSTOM_END_TIME',
        payload: customEndTime,
        isPureBin,
        isAuctionBin,
        isChangeItem: getSyiSlice(getState()).syi.isEditOfPublishedItem
    });
    dispatch(feesActions.updatePrices());
};

const setDurationDays = durationDays => (dispatch, getState) => {
    const price = getSyiSlice(getState()).price;
    const hasOpeningBid = !!price.openingBid;
    const hasAuctionBinPrice = !!price.auctionBinPrice;
    const isAuctionBin = hasOpeningBid && hasAuctionBinPrice;
    const isPureBin = !hasOpeningBid && hasAuctionBinPrice;
    dispatch({
        type: 'SET_DURATION_DAYS',
        payload: durationDays,
        isPureBin,
        isAuctionBin,
        isChangeItem: getSyiSlice(getState()).syi.isEditOfPublishedItem
    });
    dispatch(feesActions.updatePrices());
};

const resetDurationAndEndTime = () => (dispatch, getState) => {
    const price = getSyiSlice(getState()).price;
    const hasOpeningBid = !!price.openingBid;
    const hasAuctionBinPrice = !!price.auctionBinPrice;
    const isAuctionBin = hasOpeningBid && hasAuctionBinPrice;
    const isPureBin = !hasOpeningBid && hasAuctionBinPrice;
    dispatch({
        type: 'RESET_DURATION_AND_END_TIME',
        isPureBin,
        isAuctionBin
    });
    dispatch(feesActions.updatePrices());
};

const setRestart = restart => ({
    type: 'SET_RESTART',
    payload: restart
});

const setDisplayPromoted = displayPromoted => dispatch => {
    dispatch({
        type: 'SET_DISPLAY_PROMOTED',
        payload: displayPromoted || false
    });
    dispatch(feesActions.updatePrices());
};

const setDisplayHighlighted = displayHighlighted => dispatch => {
    dispatch({
        type: 'SET_DISPLAY_HIGHLIGHTED',
        payload: displayHighlighted || false
    });
    dispatch(feesActions.updatePrices());
};

const setReference = reference => ({
    type: 'SET_REFERENCE',
    payload: reference
});

const trackAttributeSuggestions = (
    draftSelectedAttributes,
    suggestedAttributes,
    suggestionEngine,
    completionTimeInSeconds,
    isNewDraft
) => {
    const comparer = (a, b) => a.localeCompare(b);

    draftSelectedAttributes.forEach(attribute => {
        const label = [
            suggestionEngine,
            attribute.id,
            attribute.backingField,
            attribute.values?.sort(comparer).join(','),
            suggestedAttributes
                .find(sa => sa.id === attribute.id)
                ?.values?.sort(comparer)
                .join(','),
            completionTimeInSeconds,
            !!isNewDraft
        ];
        const analyticsData = {
            category: 'MachineLearning',
            action: 'SyiAttributeSuggestion',
            label: label.join(';')
        };
        googleTagManager.trackEvent(analyticsData);
    });
};

const publish = translator => (dispatch, getState) => {
    dispatch({ type: 'SET_PUBLISH_PENDING' });

    const state = getSyiSlice(getState());
    const { displayHighlighted, displayPromoted } = state.extraFeatures;
    const reservePrice = state.price.reservePrice;
    const customEndTime = draftService.getCustomEndTime(state.itemDuration);
    const draft = draftService.buildDraftFromState(state);
    var splitTestGroups = getState().environment.splitTestGroups;
    var featureSwitches = getState().environment.featureSwitches;

    publishService
        .publish(draft, state.memberConsent.hasTraderaPayConsent)
        .then(result => {
            if (displayHighlighted) {
                googleTagManager.trackAddonBeforePublish('Highlight');
            }
            if (displayPromoted) {
                googleTagManager.trackAddonBeforePublish('Rank up');
            }
            if (reservePrice) {
                googleTagManager.trackAddonBeforePublish('Reserve price');
            }
            if (customEndTime) {
                googleTagManager.trackAddonBeforePublish('End time');
            }

            if (featureSwitches['syi-attribute-suggestions']) {
                const draftSelectedAttributes =
                    draft.attributeValues.termAttributeValues;
                const suggestedAttributes =
                    selectSuggestedAttributes(getState()) || [];
                trackAttributeSuggestions(
                    draftSelectedAttributes,
                    suggestedAttributes,
                    splitTestGroups['attribute_suggestion_extractor'],
                    Math.round(sessionService.getElapsedMillis() / 1000),
                    state.draft.isNewDraft
                );
            }

            dispatch(setDraft({}));
            navigationService.goToPublished(result.itemId);
        })
        .catch(result => {
            let response = result.responseJSON;
            if (
                hasResponseErrorCode(response, [
                    'DraftAlreadyPublished',
                    'DraftBelongsToAnotherMember'
                ])
            ) {
                dispatch(setDraft({}));
                navigationService.goToStart();
            } else if (hasResponseErrorCode(response, ['ValidationError'])) {
                handleServerValidationError(
                    response.errors || [],
                    dispatch,
                    translator
                );
            } else {
                dispatch({ type: 'SET_PUBLISH_FAILED' });
                dispatch(syiActions.toggleModal('error-modal-publish-failed'));
            }
        });
};

const hasResponseErrorCode = (responseOrNull, arrayOfErrorCodesToCheck) =>
    responseOrNull &&
    arrayOfErrorCodesToCheck.includes(responseOrNull.errorCode);

const loadDraftIfMissing = draftId => (dispatch, getState) => {
    var draft = getSyiSlice(getState()).draft;
    const isAlreadyLoaded = draft.draftId === draftId;
    if (isAlreadyLoaded) {
        return Promise.resolve(draft);
    }
    return loadDraft(dispatch, draftId);
};

const loadDefaultDraft = ({ charityId }) => dispatch => {
    dispatch(syiActions.reset());
    dispatch(categoryActions.reset());
    dispatch(resetCharityAndShippingInfo());
    dispatch(setDraft(draftService.loadDefaultDraft(), { charityId }));
    dispatch(shippingActions.resetSelected());
};

const loadDefaultTemplateDraft = () => dispatch => {
    dispatch(syiActions.reset());
    dispatch(categoryActions.reset());
    dispatch(resetCharityAndShippingInfo());
    var template = draftService.loadDefaultTemplateDraft();
    template.templateId = 'new';
    dispatch(setDraft(template));
};

const useDraftAsTemplate = () => dispatch => {
    dispatch({
        type: 'SET_DRAFT_REASON',
        payload: 'Template'
    });
    dispatch({
        type: 'SET_DRAFT_ID',
        payload: null
    });
};

const setDraftVersion = version => dispatch => {
    dispatch({
        type: 'SET_DRAFT_VERSION',
        payload: version
    });
};

export const triggerSave = () => (dispatch, getState) =>
    saveDraft(dispatch, getState);

const saveDraft = (dispatch, getState) => {
    let draft = draftService.buildDraftFromState(getSyiSlice(getState()));
    let version = getSyiSlice(getState()).draft.version || 0;
    return draftService
        .saveDraft(draft)
        .then(result => {
            if (draft.draftId !== result.draftId) {
                dispatch({
                    type: 'SET_DRAFT_ID',
                    payload: result.draftId
                });
            }
            if (version > 0 && version + 1 !== result.version) {
                // Unexpected version. The draft is being edited somewhere else.
            }
            const wasNewDraftWithoutId = !draft.draftId && !!result.draftId;
            if (wasNewDraftWithoutId) {
                navigationService.setDraftUrlWithoutReload(result.draftId);
            }
            // Must happen after new URL is set for re-render to happen.
            dispatch(setDraftVersion(result.version));
            return result;
        })
        .catch(result => {
            let response = result.responseJSON;
            if (
                hasResponseErrorCode(response, [
                    'DraftAlreadyPublished',
                    'DraftBelongsToAnotherMember'
                ])
            ) {
                dispatch(setDraft({}));
                navigationService.goToStart();
            } else if (result.status == 404) {
                dispatch(setDraft({}));
                navigationService.goToStart();
            }
            throw result;
        });
};

const setUsedAllowed = isAllowed => ({
    type: 'SET_ITEM_CONDITION_SELECTION_ERROR',
    payload: isAllowed ? null : 'syi_validation_used_not_allowed_for_category'
});

const setItemCondition = condition => dispatch => {
    dispatch({
        type: 'SET_ITEM_CONDITION',
        payload: condition
    });
    dispatch(updateItemConditionRestrictionForCategory());
};

const updateItemConditionRestrictionForCategory = () => (
    dispatch,
    getState
) => {
    const condition = getSyiSlice(getState()).itemCondition.itemCondition;
    const isUsed = condition === 'Used';
    if (isUsed) {
        const isAllowed = isUsedAllowed(getState);
        dispatch(setUsedAllowed(isAllowed));
    } else {
        dispatch(setUsedAllowed(true));
    }
};

const isUsedAllowed = getState => {
    const categoryRestriction = getSyiSlice(getState()).syi.categoryRestriction;
    return !categoryRestriction || categoryRestriction.allowUsed !== false;
};

const resetItemCondition = () => ({
    type: 'RESET_ITEM_CONDITION'
});

const publishAdditionalDescription = text => (dispatch, getState) => {
    if (text && text.length > 0) {
        dispatch({
            type: 'ADD_ADDITIONAL_DESCRIPTION',
            payload: text
        });
    }
    dispatch({ type: 'SET_PUBLISH_PENDING' });
    let draft = draftService.buildDraftFromState(getSyiSlice(getState()));
    publishService
        .publish(draft)
        .then(result => {
            navigationService.goToPublished(result.itemId);
            dispatch(setDraft({}));
        })
        .catch(() => {
            dispatch({ type: 'SET_PUBLISH_FAILED' });
        });
};

const resetDraft = () => ({ type: 'RESET_DRAFT' });

const resetDrafts = () => dispatch => {
    dispatch({
        type: 'SET_DRAFTS',
        payload: null
    });
};

const setCurrency = currency => ({
    type: 'SET_CURRENCY',
    payload: { currency }
});

export default {
    setDraft,
    loadDraft,
    loadDrafts,
    loadImagesFromDraft,
    canBeResumed,
    updateDraftCanBeResumed,
    setCustomEndTime,
    setDurationDays,
    resetDurationAndEndTime,
    setRestart,
    setDisplayPromoted,
    setDisplayHighlighted,
    setReference,
    publish,
    loadDraftIfMissing,
    loadDefaultDraft,
    loadDefaultTemplateDraft,
    useDraftAsTemplate,
    saveDraft,
    setItemCondition,
    resetItemCondition,
    publishAdditionalDescription,
    resetDraft,
    resetDrafts,
    updateItemConditionRestrictionForCategory,
    setIsLoading
};
